import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { useAuth } from "../AuthProvider";
import { db } from "../firebase";
import { summarizeStorePackages } from "../utils/storePackages";

const ProtectedRoute = ({
  children,
  allowExpiredPackage = false,
  requireActivePackage = false,
}) => {
  const { user, authLoading } = useAuth();
  const location = useLocation();
  const [userDoc, setUserDoc] = useState(null);
  const [docLoading, setDocLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) {
      setUserDoc(null);
      setDocLoading(false);
      return;
    }

    setDocLoading(true);
    const unsub = onSnapshot(
      doc(db, "users", user.uid),
      (snap) => {
        setUserDoc(snap.exists() ? snap.data() : null);
        setDocLoading(false);
      },
      () => {
        setUserDoc(null);
        setDocLoading(false);
      },
    );

    return () => unsub();
  }, [user?.uid]);

  const hasExpiredSubscription = useMemo(() => {
    const packageState = summarizeStorePackages(userDoc || {});
    return packageState.summary.status === "expired";
  }, [userDoc]);

  const hasActivePackage = useMemo(() => {
    const packageState = summarizeStorePackages(userDoc || {});
    return packageState.hasActivePackage;
  }, [userDoc]);

  if (authLoading || docLoading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!allowExpiredPackage && hasExpiredSubscription) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/store-packages?expired=1&next=${next}`} replace />;
  }

  if (requireActivePackage && !hasActivePackage) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export default ProtectedRoute;
